import { useAuth0 } from '@auth0/auth0-react';
import React, { createContext, useContext, useEffect, useState } from 'react';

interface ThemeContextValue {
  isDark: boolean;
  toggle: () => void;
}

const ThemeContext = createContext<ThemeContextValue>({
  isDark: false,
  toggle: () => {
    // Default no-op implementation, overridden by ThemeProvider
  },
});

export const ThemeProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { user } = useAuth0();

  const getStored = () => {
    if (typeof window === 'undefined') return false;
    const key = user ? `theme_${user.sub}` : 'theme';
    const stored = localStorage.getItem(key);
    if (stored) return stored === 'dark';
    // Default to light mode for new users regardless of system preferences
    return false;
  };

  const [isDark, setIsDark] = useState<boolean>(getStored);

  useEffect(() => {
    setIsDark(getStored());
  }, [user]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const root = document.documentElement;
    const key = user ? `theme_${user.sub}` : 'theme';
    if (isDark) {
      root.classList.add('dark');
      localStorage.setItem(key, 'dark');
    } else {
      root.classList.remove('dark');
      localStorage.setItem(key, 'light');
    }
  }, [isDark, user]);

  const toggle = () => setIsDark(prev => !prev);

  return (
    <ThemeContext.Provider value={{ isDark, toggle }}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = () => useContext(ThemeContext);
